// src/services/dynamicForms/FormConfigService.ts

import { PdfTemplateMapping } from "../../interfaces/dynamicForms/PdfMapping";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// --- Interface for the aggregated data structure ---
export interface AggregatedConfigs {
    [formId: string]: {
        config?: string; // JSON string content for CONFIG
        rules?: string;  // JSON string content for RULES
        mapping?: string; // JSON string content for MAPPING
    };
}

/**
 * Fetches a specific mapping configuration JSON string.
 * NOTE: This might become less necessary if using the aggregated endpoint primarily.
 */
export async function getMappingConfiguration(
    mappingKey: string,
    authToken: string,
    version: string = 'latest'
): Promise<string | null> {
    // ... (your existing implementation) ...
    const configType = "MAPPING";
    const encodedIdentifier = encodeURIComponent(mappingKey);
    const encodedVersion = encodeURIComponent(version);
    const url = `${API_BASE_URL}/form/specific/${configType}/${encodedIdentifier}?version=${encodedVersion}`; // Use specific route if available
    console.log(`[FormConfigService] Fetching specific mapping from: ${url}`);
     try {
        const response = await fetch(url, { /* ... headers ... */ });
        if (response.status === 404) return null;
        if (!response.ok) throw new Error(/* ... */);
        const jsonContent = await response.text();
        JSON.parse(jsonContent); // Validate
        return jsonContent;
    } catch (error) { /* ... error handling ... */ throw error; }
}

/**
 * Saves (creates or updates) a specific mapping configuration.
 * NOTE: Consider replacing calls to this with the generic saveSpecificConfiguration
 */
export async function saveMappingConfiguration(
    mappingKey: string, // This is the 'identifier' for mapping type
    mappingData: PdfTemplateMapping | object, // Accept object too
    authToken: string,
    version?: string,
    description?: string
): Promise<void> {
     console.warn("[FormConfigService] Consider using saveSpecificConfiguration instead of saveMappingConfiguration.");
     // Delegate to the generic save function
     await saveSpecificConfiguration(
        "MAPPING",
        mappingKey, // Use mappingKey as the identifier for MAPPING
        JSON.stringify(mappingData, null, 2), // Stringify the object
        authToken,
        version,
        description
     );
}

// --- NEW Function to fetch aggregated configurations ---
/**
 * Fetches all active 'latest' configurations aggregated by FormId.
 */
export async function getAggregatedConfigurations(
    authToken: string,
    version: string = 'latest'
): Promise<AggregatedConfigs> {
    const url = `${API_BASE_URL}/form/aggregated-configs?version=${encodeURIComponent(version)}`;
    console.log(`[FormConfigService] Fetching aggregated configurations from: ${url}`);

    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${authToken}`,
                'Accept': 'application/json',
            },
        });

        if (!response.ok) {
            let errorDetails = response.statusText;
            try {
                 const errorBody = await response.json();
                 errorDetails = errorBody?.message || JSON.stringify(errorBody) || errorDetails;
            } catch { /* Ignore */ }
            throw new Error(`Failed to fetch aggregated configurations: ${response.status} - ${errorDetails}`);
        }

        const data: AggregatedConfigs = await response.json();
        console.log(`[FormConfigService] Successfully fetched aggregated configurations.`);
        return data;

    } catch (error) {
        console.error('[FormConfigService] Error in getAggregatedConfigurations:', error);
        throw error; // Re-throw for the component
    }
}


// --- NEW Generic Function to save specific configurations ---
/**
 * Saves (creates or updates) a specific configuration (CONFIG, RULES, or MAPPING).
 */
export async function saveSpecificConfiguration(
    configType: 'CONFIG' | 'RULES' | 'MAPPING',
    identifier: string, // FormId for CONFIG/RULES, Mapping Key for MAPPING
    jsonContent: string, // The raw JSON string to save
    authToken: string,
    version?: string, // Optional version (defaults to latest on backend)
    description?: string // Optional description
): Promise<void> { // Returns void on success
    // Ensure configType is uppercase for the API URL segment
    const configTypeUpper = configType.toUpperCase();
    const url = `${API_BASE_URL}/form/${configTypeUpper}`; // POST to the type endpoint

    const payload = {
        Identifier: identifier,
        JsonContent: jsonContent, // Pass the raw JSON string directly
        Version: version,
        Description: description
    };
    console.log(`[FormConfigService] Saving configuration: Type=${configTypeUpper}, Identifier=${identifier}`);
    // console.log("[FormConfigService] Payload Content:", payload.JsonContent); // Be careful logging large JSON

    try {
        // Validate JSON content before sending
        JSON.parse(jsonContent);
    } catch (parseError) {
         console.error('[FormConfigService] Invalid JSON content provided for saving:', parseError);
         throw new Error(`Invalid JSON content format. Please correct before saving. Details: ${parseError}`);
    }

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`,
            },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            let errorDetails = response.statusText;
            try {
                 const errorBody = await response.json();
                 errorDetails = errorBody?.message || JSON.stringify(errorBody) || errorDetails;
            } catch { /* Ignore */ }
             throw new Error(`Failed to save ${configTypeUpper} configuration: ${response.status} - ${errorDetails}`);
        }

        console.log(`[FormConfigService] Successfully saved ${configTypeUpper} configuration for ${identifier}`);
        // const result = await response.json(); // Optional: process success message
        // console.log("[FormConfigService] Save response:", result);

    } catch (error) {
         console.error('[FormConfigService] Error in saveSpecificConfiguration:', error);
         throw error; // Re-throw for the component
    }
}